import { Group, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { RowSelectionState } from "@tanstack/react-table"
import {
  CancelModal,
  Loader,
  SuccessModal,
  Switch,
  Button,
  ColumnManager,
  FilterBuilder,
} from "@tfg/ui"
import _ from "lodash"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { TraceabilityStatus } from "@tfg/types/endpoints/traceability"

import { queryClient } from "~/backend/queryClient"
import { InlineBanner } from "~/components/InlineBanner"
import { SortBuilder } from "~/components/SortBuilder"
import { CustomTable } from "~/components/table/CustomTable"
import { SearchBar } from "~/components/table/SearchBar"
import {
  TABLE_CONTEXT_MAP,
  ViewHeader,
} from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import {
  FindProductsTraceabilityQueryResponse,
  FindProductsTraceabilityRequestFiltersDto,
  findTraceabilityJobsByStatusHttpControllerFindTraceabilityJobsByStatusPathParamsStatusEnum as TraceabilityJobStatusEnum,
  findTraceabilityJobsByStatusQueryKey,
  getTraceabilityCampaignStatsQueryKey,
  useFindProductsTraceability,
} from "~/gen/api"
import { useGetColumnManagerTranslations } from "~/hooks/table"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { useGetOrganizationSettingsQuery } from "~/pages/Brand/CommunicationStudio/OrganizationGeneralSettings/hooks/useGetOrganizationSettingsQuery"
import { LaunchTraceabilityModal } from "~/pages/Brand/Products/List/Components/LaunchTraceabilityModal"
import { useBuildTraceabilitySorts } from "~/pages/Brand/Products/List/Components/ProductsTables/traceability/hooks/useBuildTraceabilitySorts"
import { useTraceabilityTable } from "~/pages/Brand/Products/List/Components/ProductsTables/traceability/useTraceabilityTable"
import { useBuildProductFilters } from "~/pages/Brand/Products/List/hooks/useBuildProductFilters"
import { useAuth } from "~/store/auth-context.tsx"

export const TraceabilityProductsTable = () => {
  const [traceabilityModalOpened, traceabilityModalActions] =
    useDisclosure(false)

  const [cancelModalOpened, cancelModalActions] = useDisclosure(false)
  const [successModalOpened, successModalActions] = useDisclosure(false)

  const [showOnlyMainFabric, setShowOnlyMainFabric] = useState<boolean>(false)
  const { t } = useTranslation(["products", "common", "traceability"])
  const columnManagerTranslations = useGetColumnManagerTranslations()
  const { user, isAdmin } = useAuth()
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState<boolean>(false)
  const { data: org } = useGetOrganizationSettingsQuery(user?.organization?.id)

  const { title, searchPlaceholder, options, filtersValue, setFiltersValue } =
    useBuildProductFilters<FindProductsTraceabilityRequestFiltersDto>([
      {
        label: t("lists.placeholders.duplicateSites"),
        key: "duplicateSites",
        options: [
          { label: t("lists.placeholders.all"), value: "all" },
          {
            value: "redundant",
            label: t("lists.placeholders.redundant"),
          },
        ],
        type: "select",
      },
    ])

  const [shouldKeepPulling, setShouldKeepPulling] = useState<boolean>(false)

  const { sorts, setSorts, sortOptions } = useBuildTraceabilitySorts()

  const {
    data: productsData,
    isPending: isLoadingProducts,
    isFetching: isFetchingProducts,
    setPageBasedPagination,
    pageBasedPagination,
    totalPages,
  } = usePaginatedQuery<
    typeof useFindProductsTraceability,
    FindProductsTraceabilityQueryResponse
  >(
    useFindProductsTraceability,
    {
      filters: filtersValue,
      sorts,
    },
    {
      ...(shouldKeepPulling ? { query: { refetchInterval: 5000 } } : {}),
    }
  )

  useEffect(() => {
    const products = productsData?.data ?? []

    setShouldKeepPulling(
      products.some((p) => p.traceability.status === TraceabilityStatus.PENDING)
    )
  }, [productsData])

  const products = productsData?.data ?? []

  const deepestRank =
    _.max(products.map((p) => p.operationTree?.maxRank ?? 0)) ?? 0

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const table = useTraceabilityTable({
    setPagination: setPageBasedPagination,
    setRowSelection,
    pagination: pageBasedPagination,
    rowSelection,
    pageCount: totalPages,
    maxRank: deepestRank,
    showOnlyMainFabric,
    products,
  })

  let adminMessage: string | null = null

  if (org?.settings?.zalandoPlan === "ENABLED") {
    if (isAdmin) {
      adminMessage =
        "You are seeing the content of this page because you are an admin. Non-admin user see a message explaining that they need to upgrade their plan to see this content. Go to the organization settings page to change the plan."
    } else {
      return (
        <InlineBanner
          type="info"
          size="xs"
          message={t("simulation.notSubscribed.title")}
        >
          {t("simulation.notSubscribed.message")}
        </InlineBanner>
      )
    }
  }

  return (
    <>
      <Stack>
        {adminMessage && (
          <InlineBanner
            type="admin"
            size="xs"
            message={t("simulation.notSubscribed.title")}
          >
            {adminMessage}
          </InlineBanner>
        )}
        <Views tableContext={TABLE_CONTEXT_MAP.PRODUCTS_TRACEABILITY} />
        <Group justify="space-between">
          <Group>
            <SearchBar
              filtersValue={filtersValue}
              setFiltersValue={setFiltersValue}
              placeholder={t("products:lists.placeholders.searchProduct")}
              isFetching={isFetchingProducts}
            />
            <SortBuilder
              sorts={sorts}
              setSorts={setSorts}
              options={sortOptions}
            />
            <FilterBuilder
              title={title}
              searchPlaceholder={searchPlaceholder}
              options={options}
              setFilters={setFiltersValue}
              filters={filtersValue}
            />
            <ColumnManager
              table={table}
              translations={columnManagerTranslations}
              isOpen={isColumnManagerOpen}
              setIsOpen={setIsColumnManagerOpen}
            />
            <Switch
              label={t("common:showOnlyMainFabric")}
              checked={showOnlyMainFabric}
              onChange={() => setShowOnlyMainFabric((v) => !v)}
            />
            <ViewHeader
              tableContext={TABLE_CONTEXT_MAP.PRODUCTS_TRACEABILITY}
            />
          </Group>
          {isAdmin && productsData?.count ? (
            <Button
              onClick={() => {
                const queryKeys = [
                  getTraceabilityCampaignStatsQueryKey(),
                  findTraceabilityJobsByStatusQueryKey(
                    TraceabilityJobStatusEnum.RUNNING
                  ),
                ]
                queryKeys.forEach((queryKey) => {
                  queryClient.invalidateQueries({ queryKey })
                })
                traceabilityModalActions.open()
              }}
              toolTip={t("traceability.action.tooltip")}
              disabled={productsData?.count === 0}
            >
              {t("traceability.action.label")}
            </Button>
          ) : null}
        </Group>
        {isLoadingProducts ? (
          <Loader fullPage />
        ) : (
          <CustomTable
            table={table}
            manualPaginationCount={productsData?.count || 0}
            stickyHeader
            layout="fixed"
            setOpenColumnManager={setIsColumnManagerOpen}
          />
        )}
      </Stack>
      <LaunchTraceabilityModal
        opened={traceabilityModalOpened}
        cancelModalOpened={cancelModalOpened}
        onClose={traceabilityModalActions.close}
        onSuccess={successModalActions.open}
        onCancel={cancelModalActions.open}
      />
      <CancelModal
        opened={cancelModalOpened}
        onClose={cancelModalActions.close}
        onCancel={traceabilityModalActions.close}
        title={t("traceability:launch.modal.cancel.title")}
        yes={t("traceability:launch.modal.cancel.yes")}
        no={t("traceability:launch.modal.cancel.no")}
      />
      <SuccessModal
        opened={successModalOpened}
        onClose={successModalActions.close}
        title={t("traceability:launch.modal.success.title")}
        description={t("traceability:launch.modal.success.description")}
        successButtonLabel={t("traceability:launch.modal.success.button")}
      />
    </>
  )
}
